// Aggregator local para la pantalla ProgresoPaciente. NO va en
// src/lib/supabase/queries porque no llama Supabase directamente —
// compone useAdherenciaPaciente + una query mínima de sesiones para
// derivar racha + weekly completions cliente-side.
//
// Sprint 4 · Build "Adherencia real end-to-end (fase 1 analytics)".
//
// Retorna todo lo que la pantalla necesita para las 3 stat cards + weekly
// strip. Estados de loading + errores individuales.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import {
  useAdherenciaPaciente,
  type AdherenciaResult,
} from "@/lib/supabase/queries/useAdherenciaPaciente";
import { useCurrentPacienteId } from "@/app/components/auth/AuthProvider";

export interface WeekDay {
  /** 'L', 'M', 'M', 'J', 'V', 'S', 'D' según orden L..D. */
  day: string;
  /** true si el paciente completó al menos 1 sesión ese día
   *  (últimos 7 días rolling, no calendario). */
  completed: boolean;
}

export interface ProgresoPacienteData {
  adherencia: AdherenciaResult | null;
  adherenciaLoading: boolean;
  sesionesTotal: number;
  /** Sesiones completadas en los últimos 7 días (rolling). */
  sesionesSemana: number;
  /** Días seguidos con al menos 1 sesión completada (racha actual). */
  rachaDias: number;
  weekCompletions: WeekDay[];
  /** null si aún no se resolvió el paciente (loading auth) o si es
   *  huérfano. Callers deben renderizar empty state en ese caso. */
  hasPaciente: boolean;
  loading: boolean;
  error: string | null;
}

const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];

/** Devuelve el índice L..D de una fecha (0 = L, 6 = D), tz Argentina. */
function argentinaDayIndex(d: Date): number {
  // getDay(): 0 = Sun, 1 = Mon, ..., 6 = Sat. Mapeamos a L..D (0..6).
  const g = d.getDay();
  return g === 0 ? 6 : g - 1;
}

/** Fecha YYYY-MM-DD en tz local del cliente (paciente en Argentina).
 *  Servía como key para "un día". */
function localDateKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Cuenta la racha actual: días consecutivos hacia atrás desde hoy
 *  con al menos 1 sesión completada. Si hoy no tiene sesión y ayer sí,
 *  la racha arranca desde ayer (permite que la racha "sobreviva" el
 *  día en curso). Si ni hoy ni ayer tienen, racha = 0. */
function computeRachaDias(sessionDateKeys: Set<string>): number {
  const now = new Date();
  let racha = 0;
  // Empezamos desde hoy. Si hoy no tiene, aún permitimos ayer como start.
  for (let offset = 0; offset < 365; offset++) {
    const d = new Date(now.getTime() - offset * 86_400_000);
    const key = localDateKey(d.toISOString());
    if (sessionDateKeys.has(key)) {
      racha++;
    } else {
      // Excepción: si offset===0 (hoy) y no completó, no cortamos
      // todavía — chequeamos ayer. Si tampoco, cortamos.
      if (offset === 0) {
        // seguimos al ayer sin sumar
        continue;
      }
      break;
    }
  }
  return racha;
}

/** Weekly completions strip: L-D con boolean per día de los últimos
 *  7 días (rolling, empezando 6 días atrás y terminando hoy). El label
 *  del día refleja el día real (L..D) según la fecha, no fijo. */
function computeWeekCompletions(sessionDateKeys: Set<string>): WeekDay[] {
  const result: WeekDay[] = [];
  const now = new Date();
  for (let offset = 6; offset >= 0; offset--) {
    const d = new Date(now.getTime() - offset * 86_400_000);
    const key = localDateKey(d.toISOString());
    result.push({
      day: DAY_LABELS[argentinaDayIndex(d)],
      completed: sessionDateKeys.has(key),
    });
  }
  return result;
}

export function useProgresoPaciente(): ProgresoPacienteData {
  const pacienteId = useCurrentPacienteId();
  const { data: adherencia, loading: adherenciaLoading } = useAdherenciaPaciente(
    pacienteId,
    "total",
  );

  const [sesionesState, setSesionesState] = useState<{
    startedAts: string[];
    loading: boolean;
    error: string | null;
  }>({ startedAts: [], loading: !!pacienteId, error: null });

  useEffect(() => {
    if (!pacienteId) {
      setSesionesState({ startedAts: [], loading: false, error: null });
      return;
    }
    const signal = { cancelled: false };
    setSesionesState((s) => ({ ...s, loading: true, error: null }));

    void (async () => {
      const { data, error } = await supabase
        .from("sesiones")
        .select("started_at")
        .eq("paciente_id", pacienteId)
        .eq("estado", "completada")
        .order("started_at", { ascending: false });

      if (signal.cancelled) return;

      if (error) {
        setSesionesState({
          startedAts: [],
          loading: false,
          error: error.message,
        });
        return;
      }

      setSesionesState({
        startedAts: (data ?? [])
          .map((r) => (r as { started_at: string | null }).started_at)
          .filter((v): v is string => typeof v === "string"),
        loading: false,
        error: null,
      });
    })();

    return () => {
      signal.cancelled = true;
    };
  }, [pacienteId]);

  const sessionDateKeys = new Set(
    sesionesState.startedAts.map((iso) => localDateKey(iso)),
  );
  const rachaDias = computeRachaDias(sessionDateKeys);
  const weekCompletions = computeWeekCompletions(sessionDateKeys);

  return {
    adherencia,
    adherenciaLoading,
    sesionesTotal: sesionesState.startedAts.length,
    sesionesSemana: sesionesState.startedAts.filter(
      (iso) => Date.now() - new Date(iso).getTime() < 7 * 86_400_000,
    ).length,
    rachaDias,
    weekCompletions,
    hasPaciente: !!pacienteId,
    loading: sesionesState.loading || adherenciaLoading,
    error: sesionesState.error,
  };
}
